import type React from 'react'
import { type MutableRefObject, useCallback, useEffect, useRef } from 'react'

type UseClickOutside<T extends HTMLElement> = {
  onClick: (e: React.MouseEvent<T>) => void
  ref: MutableRefObject<T>
}

const useClickOutside = <T extends HTMLElement>(
  isHandled: boolean,
  onClickOut: (e: React.MouseEvent<any>) => void,
  onClickIn?: (e: React.MouseEvent<T>) => void,
  container?: HTMLElement | null
): UseClickOutside<T> => {
  const ref = useRef<T | null>(null)

  const handleDocumentClick = useCallback(
    // @ts-expect-error
    (event) => {
      const isTouch = event.type === 'touchend'

      if (event.type === 'click' && isTouch) {
        return false
      }

      if (
        !ref?.current?.contains(event.target) &&
        (!container || container.contains(event.target)) &&
        isHandled
      ) {
        onClickOut(event)
      }

      return true
    },
    [onClickOut, isHandled]
  )

  const onClick = (e: React.MouseEvent<T>) => {
    if (onClickIn) {
      onClickIn(e)
    }
  }

  useEffect(() => {
    document.addEventListener('touchend', handleDocumentClick, true)
    document.addEventListener('mousedown', handleDocumentClick, true)

    return () => {
      document.removeEventListener('touchend', handleDocumentClick, true)
      document.removeEventListener('mousedown', handleDocumentClick, true)
    }
  })

  return { onClick, ref: ref as MutableRefObject<T> }
}

export default useClickOutside
